import classNames from 'classnames';
import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { CustomPopover, Tooltip } from '@meitu/saas-ui';
import { AlignType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import './index.less';
import type { GroupLayer } from '@meitu/saas-editor-core/es/layers/group-layer';

interface IProps extends HTMLAttributes<HTMLDivElement> {
    id: string;
    lock: boolean;
    onAlign?: (type: AlignType) => void;
}

const ComposeInspector: React.FC<IProps> = ({ id, lock, onAlign }) => {
    const { $t } = useLocaleStore();
    const editor = useEditor();

    const data = getData(id);

    const [disabledDistribution, setDisabledDistribution] = useState(data.disabledDistribution);
    const [distribution, setDistribution] = useState('');
    const [alignType, setAlignType] = useState<AlignType>(data.alignType);
    const [visible, setVisible] = useState(false);

    const preIsExtraGroup = useRef(data.isExtraGroup);
    const isCanvasAlign = alignType === AlignType.canvas;

    useEffect(() => {
        setVisible(false);
    }, [alignType]);

    useEffect(() => {
        const handleLayerChange = () => {
            const data = getData(id);
            setDisabledDistribution(data.disabledDistribution);
            if (preIsExtraGroup.current !== data.isExtraGroup) {
                setAlignType(data.alignType);
                preIsExtraGroup.current = data.isExtraGroup;
            }
        };

        // id改变的时候 也需要触发更新
        handleLayerChange();

        //图层属性改变触发更新
        const handler = editor.services.flatLayer.onChange(() => {
            handleLayerChange();
        }, id);
        return () => editor.off(handler);
    }, [id]);

    function getData(id: string) {
        const layer = editor.services.flatLayer.getLayer(id);
        const children = layer?.children || [];
        const disabledDistribution = children.length < 3;
        const isGroup = editor.getInstance<GroupLayer>('GroupLayer').checkIsGroup(layer);
        const isExtraGroup = editor.getInstance<GroupLayer>('GroupLayer').checkIsExtraGroup(layer);
        const alignType = !isGroup ? AlignType.canvas : isExtraGroup ? AlignType.group : AlignType.canvas;
        return {
            disabledDistribution,
            alignType,
            isGroup,
            isExtraGroup,
        };
    }

    function doAlign(type: string) {
        if (isCanvasAlign) {
            editor.layer.canvasAlign[type](id);
        } else {
            editor.layer.groupAlign[type](id);
        }
    }

    const handleClick = (val: string) => {
        setDistribution(val);
        if (val === 'horizontal') {
            doAlign('horizontalDistribution');
            onAlign?.(alignType);
        } else if (val === 'vertical') {
            doAlign('verticalDistribution');
            onAlign?.(alignType);
        }
        setVisible(false);
    };

    const alignContent = () => {
        const data = [
            {
                icon: 'HorizontalDistributionBold',
                label: $t((v) => v.baseToolbar.horizontal),
                value: 'horizontal',
            },
            {
                icon: 'VerticalDistributionBold',
                label: $t((v) => v.baseToolbar.vertical),
                value: 'vertical',
            },
        ];
        return (
            <div className="c-base-toolbar-controller-align">
                {data.map((item) => {
                    return (
                        <div
                            key={item.value}
                            className={classNames(
                                'c-base-toolbar-controller-align-item',
                                distribution === item.value && 'c-base-toolbar-controller-align-item--active',
                            )}
                            onClick={(e) => {
                                handleClick(item.value);
                            }}
                        >
                            <IconFont fontSize={16} name={item.icon} />
                            {item.label}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="c-base-toolbar-controller">
            <LabelAction title={$t((v) => v.baseToolbar.compose)} align>
                <div className="c-base-toolbar-controller-content">
                    <Tooltip placement="bottom" title={$t((v) => v.baseToolbar.alignLeft)}>
                        <div
                            className={classNames('c-base-toolbar-controller-item', lock && 'disabled')}
                            onClick={() => {
                                if (lock) return;
                                doAlign('left');
                                onAlign?.(alignType);
                            }}
                        >
                            <IconFont fontSize={14} name="AlignLeftBold" />
                        </div>
                    </Tooltip>
                    <Tooltip placement="bottom" title={$t((v) => v.baseToolbar.alignCenter)}>
                        <div
                            className={classNames('c-base-toolbar-controller-item', lock && 'disabled')}
                            onClick={() => {
                                if (lock) return;
                                doAlign('horizontalCenter');
                                onAlign?.(alignType);
                            }}
                        >
                            <IconFont fontSize={14} name="AlignHorizontalCenterBold" />
                        </div>
                    </Tooltip>
                    <Tooltip placement="bottom" title={$t((v) => v.baseToolbar.alignRight)}>
                        <div
                            className={classNames('c-base-toolbar-controller-item', lock && 'disabled')}
                            onClick={() => {
                                if (lock) return;
                                doAlign('right');
                                onAlign?.(alignType);
                            }}
                        >
                            <IconFont fontSize={14} name="AlignRightBold" />
                        </div>
                    </Tooltip>
                    <Tooltip placement="bottom" title={$t((v) => v.baseToolbar.alignTop)}>
                        <div
                            className={classNames('c-base-toolbar-controller-item', lock && 'disabled')}
                            onClick={() => {
                                if (lock) return;
                                doAlign('top');
                                onAlign?.(alignType);
                            }}
                        >
                            <IconFont fontSize={14} name="AlignTopBold" />
                        </div>
                    </Tooltip>

                    <Tooltip placement="bottom" title={$t((v) => v.baseToolbar.alignVertical)}>
                        <div
                            className={classNames('c-base-toolbar-controller-item', lock && 'disabled')}
                            onClick={() => {
                                if (lock) return;
                                doAlign('verticalCenter');
                                onAlign?.(alignType);
                            }}
                        >
                            <IconFont fontSize={14} name="AlignVerticalCenterBold" />
                        </div>
                    </Tooltip>
                    <Tooltip placement="bottom" title={$t((v) => v.baseToolbar.alignBottom)}>
                        <div
                            className={classNames('c-base-toolbar-controller-item', lock && 'disabled')}
                            onClick={() => {
                                if (lock) return;
                                doAlign('bottom');
                                onAlign?.(alignType);
                            }}
                        >
                            <IconFont fontSize={14} name="AlignBottomBold" />
                        </div>
                    </Tooltip>

                    {alignType === AlignType.group && (
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: disabledDistribution || lock,
                            })}
                            onClick={() => {
                                setVisible(true);
                            }}
                            style={{ position: 'relative' }}
                        >
                            <Tooltip
                                placement="top"
                                title={
                                    disabledDistribution
                                        ? null
                                        : distribution === 'vertical'
                                          ? $t((v) => v.baseToolbar.vertical)
                                          : $t((v) => v.baseToolbar.horizontal)
                                }
                            >
                                <div className="c-base-toolbar-controller-item-icon">
                                    <IconFont
                                        fontSize={14}
                                        name={
                                            distribution === 'vertical'
                                                ? 'VerticalDistributionBold'
                                                : 'HorizontalDistributionBold'
                                        }
                                    />
                                    <IconFont
                                        name="ChevronDownBlack"
                                        fontSize={6}
                                        style={{ transform: 'scale(0.5) translateY(-4px)' }}
                                    />
                                </div>
                            </Tooltip>
                        </div>
                    )}
                    {visible ? (
                        <CustomPopover
                            setVisible={setVisible}
                            customClass="c-base-toolbar-controller-align-popover"
                            excludeClass={['c-base-toolbar-controller-align-popover']}
                            content={() => alignContent()}
                        />
                    ) : null}
                </div>
            </LabelAction>
        </div>
    );
};

export default React.memo(ComposeInspector);
